import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// Future Value Calculator — mirrors image
export default function FutureValueCalc() {
  const [N, setN] = useState(10);
  const [PV, setPV] = useState(1000);
  const [IY, setIY] = useState(6);
  const [PMT, setPMT] = useState(100);
  const [when, setWhen] = useState('end');
  const c = useMemo(() => {
    const r = IY/100;
    const b = when === 'beginning' ? 1 : 0;
    const fvP = PV * Math.pow(1 + r, N);
    const fvA = r === 0 ? PMT * N : PMT * ((Math.pow(1+r, N) - 1)/r) * (1 + r*b);
    const fv = fvP + fvA;
    const totalDeposits = PMT * N;
    const interest = fv - PV - totalDeposits;
    // Schedule
    const rows = [];
    let bal = PV;
    for (let i = 1; i <= N; i++) {
      const start = bal;
      if (when === 'beginning') bal += PMT;
      const int = bal * r;
      bal += int;
      if (when === 'end') bal += PMT;
      rows.push({ n: i, start, deposit: PMT, interest: int, end: bal });
    }
    return { fv, fvP, totalDeposits, interest, rows };
  }, [N, PV, IY, PMT, when]);
  return (
    <CalcShell look="cnet" title="Future Value Calculator" subtitle="Compute the future value of an investment with a starting amount and periodic deposits."
      about={<><p>The <b>future value</b> (FV) is what an investment will be worth at a specific time, given a starting amount, periodic deposits, and a growth rate.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Number of Periods (N)</label><input type="number" value={N} onChange={e=>setN(+e.target.value||0)}/></div>
          <div className="field"><label>Starting Amount (PV)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={PV} onChange={e=>setPV(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Interest Rate (I/Y)</label><div className="flex gap-2"><input type="number" step="0.01" value={IY} onChange={e=>setIY(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Periodic Deposit (PMT)</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={PMT} onChange={e=>setPMT(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">/ period</span></div></div>
          <div className="field"><label>PMT made at the</label>
            <div className="flex gap-4 text-sm mt-1">
              <label className="flex items-center gap-1"><input type="radio" checked={when==='beginning'} onChange={()=>setWhen('beginning')}/> beginning</label>
              <label className="flex items-center gap-1"><input type="radio" checked={when==='end'} onChange={()=>setWhen('end')}/> end</label>
              <span className="text-slate-500">of each compound period</span>
            </div>
          </div>
        </div>
        <div>
          <div className="result-panel mb-4"><div className="result-title">Future Value</div><div className="result-value" data-testid="fv-result">{fmt(c.fv)}</div></div>
          <table className="data-table"><tbody>
            <tr><td>PV (Present Value)</td><td className="text-right">{fmt(PV)}</td></tr>
            <tr><td>Total Periodic Deposits</td><td className="text-right">{fmt(c.totalDeposits)}</td></tr>
            <tr><td>Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
          </tbody></table>
          <div className="mt-4"><PieChart parts={[{label:'Starting amount',value:PV,color:'#2563eb'},{label:'Periodic deposits',value:c.totalDeposits,color:'#10b981'},{label:'Interest',value:c.interest,color:'#dc2626'}]} size={180}/></div>
        </div>
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Balance Accumulation Graph</h3>
        <BarChart
          data={c.rows.map(r => ({
            label: String(r.n),
            values: [
              { value: PV, color: '#2563eb' },
              { value: Math.max(0, r.deposit * r.n), color: '#10b981' },
              { value: Math.max(0, r.end - PV - r.deposit * r.n), color: '#f97316' },
            ]
          }))}
          stack={true}
          legend={[
            { label: 'Starting amount', color: '#2563eb' },
            { label: 'Periodic deposits', color: '#10b981' },
            { label: 'Interest', color: '#f97316' },
          ]}
        />
      </div>
      <div className="calc-card p-5 mt-6">
        <h3 className="font-bold text-slate-900 mb-3">Schedule</h3>
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white sticky top-0"><tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-right">Start balance</th><th className="px-3 py-2 text-right">Deposit</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">End balance</th></tr></thead>
            <tbody>
              {c.rows.map((r,i)=>(<tr key={i} className={`border-b ${i%2?'bg-slate-50':''}`}><td className="px-3 py-2">{r.n}</td><td className="px-3 py-2 text-right">{fmt(r.start)}</td><td className="px-3 py-2 text-right">{fmt(r.deposit)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.end)}</td></tr>))}
            </tbody>
          </table>
        </div>
      </div>
    </CalcShell>
  );
}
